import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import NavbarStaff from "./NavbarStaff";
import { useAuth } from "./AuthContext";
import { useAuthFetch } from "../hooks/useAuthFetch";
import "../stili/DashboardStaff.css";
import logoDx from "../assets/img/logo_dx.jpg";

const API_URL = process.env.REACT_APP_API_URL;

export default function DashboardStaff() {
  const { user } = useAuth();
  const authFetch = useAuthFetch();
  const navigate = useNavigate();

  const [prossimoAllenamento, setProssimoAllenamento] = useState(null);
  const [prossimaGara, setProssimaGara] = useState(null);
  const [staffMe, setStaffMe] = useState(null);
  const [assentiProssimoAllenamento, setAssentiProssimoAllenamento] = useState([]);
  const [assentiProssimaGara, setAssentiProssimaGara] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.accessToken) {
      navigate("/login");
    }
  }, [user, navigate]);

  useEffect(() => {
    if (!user?.accessToken) return;

    let mounted = true;

    const loadDashboardData = async () => {
      setLoading(true);

      try {
        const [resAllenamento, resGara, resStaff] = await Promise.all([
          authFetch(`${API_URL}/api/allenamenti/miei/prossimo`),
          authFetch(`${API_URL}/api/gare/mie/prossima`),
          authFetch(`${API_URL}/api/admin/staff/me`)
        ]);

        let allenamentoData = null;
        let garaData = null;
        let staffData = null;
        let assentiAllenamentoData = [];
        let assentiGaraData = [];

        if (resAllenamento.ok && resAllenamento.status !== 204) {
          allenamentoData = await resAllenamento.json().catch(() => null);
        }

        if (resGara.ok && resGara.status !== 204) {
          garaData = await resGara.json().catch(() => null);
        }

        if (resStaff.ok && resStaff.status !== 204) {
          staffData = await resStaff.json().catch(() => null);
        }

        if (allenamentoData?.id) {
          const resAssentiAllenamento = await authFetch(
            `${API_URL}/api/staff/presenze/allenamento/${allenamentoData.id}/assenti`
          );

          if (resAssentiAllenamento.ok && resAssentiAllenamento.status !== 204) {
            const data = await resAssentiAllenamento.json().catch(() => []);
            assentiAllenamentoData = Array.isArray(data) ? data : [];
          }
        }

        if (garaData?.id) {
          const resAssentiGara = await authFetch(
            `${API_URL}/api/staff/presenze-gara/assenti/dashboard`
          );

          if (resAssentiGara.ok && resAssentiGara.status !== 204) {
            const data = await resAssentiGara.json().catch(() => []);
            const lista = Array.isArray(data) ? data : [];

            assentiGaraData = lista.filter((item) => item.idGara === garaData.id);
          }
        }

        if (mounted) {
          setProssimoAllenamento(allenamentoData);
          setProssimaGara(garaData);
          setStaffMe(staffData);
          setAssentiProssimoAllenamento(assentiAllenamentoData);
          setAssentiProssimaGara(assentiGaraData);
        }
      } catch (error) {
        console.error("Errore caricamento dashboard staff:", error);
        if (mounted) {
          setProssimoAllenamento(null);
          setProssimaGara(null);
          setStaffMe(null);
          setAssentiProssimoAllenamento([]);
          setAssentiProssimaGara([]);
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadDashboardData();

    return () => {
      mounted = false;
    };
  }, [user, authFetch]);

  const nomeUtente =
    staffMe?.nome && staffMe?.cognome
      ? `${staffMe.nome} ${staffMe.cognome}`
      : staffMe?.nome || user?.username || "Staff";

  const formatDate = (value) => {
    if (!value) return "—";
    const d = new Date(`${value}T00:00:00`);
    return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString("it-IT");
  };

  const formatTime = (value) => {
    if (!value) return "—";
    return value?.slice?.(0, 5) || value;
  };

  const luogoGaraLabel = (gara) => {
    if (!gara?.luogo) return "Luogo non disponibile";
    if (gara.luogo === "CASA") return "In casa";
    if (gara.luogo === "TRASFERTA") return "Trasferta";
    if (gara.luogo === "NEUTRO") return "Campo neutro";
    return gara.luogo;
  };

  const renderNomeAssente = (giocatore) => {
    if (!giocatore) return "Giocatore";

    const cognome =
      giocatore.cognomeGiocatore ||
      giocatore.cognome ||
      "";

    const nome =
      giocatore.nomeGiocatore ||
      giocatore.nome ||
      "";

    return `${cognome} ${nome}`.trim() || "Giocatore";
  };

  const renderNomeAssenteGara = (giocatore) => {
    if (!giocatore) return "Giocatore";

    const cognome = giocatore.cognome || "";
    const nome = giocatore.nome || "";

    return `${cognome} ${nome}`.trim() || "Giocatore";
  };

  if (!user?.accessToken) return null;

  return (
    <div className="dashboard-staff-page">
      <div className="dashboard-staff-layout">
        <aside className="dashboard-staff-sidebar">
          <NavbarStaff />
        </aside>

        <main className="dashboard-staff-main">
          <section className="dashboard-staff-hero">
            <div className="dashboard-staff-hero-text">
              <span className="dashboard-staff-badge">Area Staff</span>
              <h1>Gestionale Attività</h1>
              <p>
                Benvenuto <strong>{nomeUtente}</strong> nella sezione riservata
                allo staff.
              </p>
            </div>

            <div className="dashboard-staff-logo-box">
              <img
                src={logoDx}
                alt="Logo gestionale"
                className="dashboard-staff-logo"
              />
            </div>
          </section>

          <section className="dashboard-staff-cards">
            <div className="dashboard-staff-card">
              <div className="dashboard-staff-card-header">
                <span className="dashboard-staff-card-label">Allenamenti</span>
              </div>

              <h2>Prossimo allenamento</h2>

              {loading ? (
                <>
                  <p className="dashboard-staff-value">Caricamento...</p>
                  <p className="dashboard-staff-subtext">
                    Recupero dati in corso
                  </p>
                  <span className="dashboard-staff-highlight">Attendere</span>
                </>
              ) : prossimoAllenamento ? (
                <>
                  <p className="dashboard-staff-value">
                    {formatDate(prossimoAllenamento.dataAllenamento)} -{" "}
                    {formatTime(prossimoAllenamento.oraInizio)}
                  </p>
                  <p className="dashboard-staff-subtext">
                    {prossimoAllenamento.nomeCategoria || "Categoria non disponibile"}
                  </p>
                  <p className="dashboard-staff-subtext">
                    {prossimoAllenamento.campo || "Campo non disponibile"}
                  </p>
                  <span className="dashboard-staff-highlight">
                    {prossimoAllenamento.stato || "ATTIVO"}
                  </span>
                </>
              ) : (
                <>
                  <p className="dashboard-staff-value">Nessuno</p>
                  <p className="dashboard-staff-subtext">
                    Non risultano allenamenti futuri programmati
                  </p>
                  <span className="dashboard-staff-highlight">
                    Nessun dato
                  </span>
                </>
              )}
            </div>

            <div className="dashboard-staff-card">
              <div className="dashboard-staff-card-header">
                <span className="dashboard-staff-card-label">Gare</span>
              </div>

              <h2>Prossima gara</h2>

              {loading ? (
                <>
                  <p className="dashboard-staff-value">Caricamento...</p>
                  <p className="dashboard-staff-subtext">
                    Recupero dati in corso
                  </p>
                  <span className="dashboard-staff-highlight">Attendere</span>
                </>
              ) : prossimaGara ? (
                <>
                  <p className="dashboard-staff-value">
                    {prossimaGara.avversario || "Avversario non disponibile"}
                  </p>
                  <p className="dashboard-staff-subtext">
                    {formatDate(prossimaGara.dataGara)} - ore{" "}
                    {formatTime(prossimaGara.oraGara)} - {luogoGaraLabel(prossimaGara)}
                  </p>
                  <p className="dashboard-staff-subtext">
                    {prossimaGara.nomeCategoria || "Categoria non disponibile"}
                  </p>
                  <span className="dashboard-staff-highlight">
                    {prossimaGara.statoGara || "PROGRAMMATA"}
                  </span>
                </>
              ) : (
                <>
                  <p className="dashboard-staff-value">Nessuna</p>
                  <p className="dashboard-staff-subtext">
                    Non risultano gare future in programma
                  </p>
                  <span className="dashboard-staff-highlight">
                    Nessun dato
                  </span>
                </>
              )}
            </div>

            <div className="dashboard-staff-card">
              <div className="dashboard-staff-card-header">
                <span className="dashboard-staff-card-label">Presenze</span>
              </div>

              <h2>Assenti prossimo allenamento</h2>

              {loading ? (
                <>
                  <p className="dashboard-staff-value">Caricamento...</p>
                  <p className="dashboard-staff-subtext">
                    Recupero assenti in corso
                  </p>
                  <span className="dashboard-staff-highlight">Attendere</span>
                </>
              ) : !prossimoAllenamento ? (
                <>
                  <p className="dashboard-staff-value">—</p>
                  <p className="dashboard-staff-subtext">
                    Nessun allenamento imminente disponibile
                  </p>
                  <span className="dashboard-staff-highlight">Nessun dato</span>
                </>
              ) : assentiProssimoAllenamento.length === 0 ? (
                <>
                  <p className="dashboard-staff-value">Nessuno</p>
                  <p className="dashboard-staff-subtext">
                    Nessun assente segnalato per il prossimo allenamento
                  </p>
                  <span className="dashboard-staff-highlight">Situazione aggiornata</span>
                </>
              ) : (
                <>
                  <div className="dashboard-staff-assenti-list">
                    {assentiProssimoAllenamento.map((g, index) => (
                      <p
                        key={
                          g.idGiocatore ||
                          g.id ||
                          `${g.cognomeGiocatore || g.cognome}-${g.nomeGiocatore || g.nome}-${index}`
                        }
                        className="dashboard-staff-subtext"
                      >
                        • {renderNomeAssente(g)}
                      </p>
                    ))}
                  </div>
                  <span className="dashboard-staff-highlight">Assenti segnalati</span>
                </>
              )}
            </div>

            <div className="dashboard-staff-card">
              <div className="dashboard-staff-card-header">
                <span className="dashboard-staff-card-label">Presenze gara</span>
              </div>

              <h2>Gestione presenza gara</h2>

              {loading ? (
                <>
                  <p className="dashboard-staff-value">Caricamento...</p>
                  <p className="dashboard-staff-subtext">
                    Recupero dati gara in corso
                  </p>
                  <span className="dashboard-staff-highlight">Attendere</span>
                </>
              ) : !prossimaGara ? (
                <>
                  <p className="dashboard-staff-value">Nessuna gara</p>
                  <p className="dashboard-staff-subtext">
                    Non ci sono gare imminenti da gestire
                  </p>
                  <span className="dashboard-staff-highlight">Nessun dato</span>
                </>
              ) : (
                <>
                  <p className="dashboard-staff-value">
                    {prossimaGara.avversario || "Avversario non disponibile"}
                  </p>
                  <p className="dashboard-staff-subtext">
                    {formatDate(prossimaGara.dataGara)} - ore{" "}
                    {formatTime(prossimaGara.oraGara)}
                  </p>
                  <p className="dashboard-staff-subtext">
                    {assentiProssimaGara.length === 0
                      ? "Nessun assente segnalato al momento"
                      : `Assenti segnalati: ${assentiProssimaGara.length}`}
                  </p>

                  {assentiProssimaGara.length > 0 && (
                    <div className="dashboard-staff-assenti-list">
                      {assentiProssimaGara.slice(0, 4).map((g, index) => (
                        <p
                          key={
                            g.idConvocazioneGiocatore ||
                            g.idGiocatore ||
                            `${g.cognome}-${g.nome}-${index}`
                          }
                          className="dashboard-staff-subtext"
                        >
                          • {renderNomeAssenteGara(g)}
                        </p>
                      ))}
                    </div>
                  )}

                  <div className="dashboard-staff-card-actions">
                    <button
                      type="button"
                      className="dashboard-staff-action-btn"
                      onClick={() => navigate("/gestionale1/presenze-gara-staff")}
                    >
                      Apri modulo
                    </button>
                  </div>
                </>
              )}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
}